
import React, { useEffect, useState } from 'react';
import { 
  Pagination, 
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
  PaginationPrevious,
  PaginationNext
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { ContentPagination } from '@/v2/services/types/serviceInterfaces';

interface PaginationControlsProps {
  pagination: ContentPagination;
  onPageChange?: (page: number) => void;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 640px)').matches);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

const PaginationControls = ({ pagination, onPageChange }: PaginationControlsProps) => {
  const [localPagination, setLocalPagination] = useState<ContentPagination>(pagination);
  const { currentPage, totalPages, hasNextPage, hasPrevPage } = localPagination;
  const isMobile = useIsMobile();

  // if (totalPages <= 0) return null;

  const handlePrevClick = () => {
    if (hasPrevPage && onPageChange) onPageChange(currentPage - 1);
  };
  const handleNextClick = () => {
    if (hasNextPage && onPageChange) onPageChange(currentPage + 1);
  };

  // Helper to generate page list with first, second, and last two pages always visible when applicable
  const getPages = () => {
    const pages = [];
    
    // Calculate the range of pages to show around current page
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const pages = getPages();
  useEffect(() => {
    setLocalPagination(pagination);
  }, [pagination]);
  return (
    <div className="mt-8 pb-2">
      <Pagination>
        <PaginationContent className="flex flex-wrap sm:flex-nowrap justify-center gap-2 sm:gap-3 overflow-x-auto whitespace-nowrap px-1">
          <PaginationItem>
            <PaginationPrevious 
              onClick={handlePrevClick} 
              className={cn(
                "cursor-pointer",
                !hasPrevPage && "opacity-50 pointer-events-none"
              )}
              aria-disabled={!hasPrevPage}
              tabIndex={hasPrevPage ? 0 : -1}
            />
          </PaginationItem>
          
          {pages.map((page, idx) => {
            if (page === 'start-ellipsis' || page === 'end-ellipsis') {
              return (
                <PaginationItem key={page + idx}>
                  <PaginationEllipsis />
                </PaginationItem>
              );
            }
            
            const isActive = page === currentPage;
            return (
              <PaginationItem key={page}>
                <PaginationLink
                  isActive={isActive}
                  onClick={() => page !== currentPage && onPageChange && onPageChange(page as number)}
                  className={cn(
                    "min-w-[40px] text-center cursor-pointer rounded-md",
                    isActive ? "bg-black border-black text-white" : "bg-white border border-gray-200"
                  )}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            );
          })}
          
          <PaginationItem>
            <PaginationNext 
              onClick={handleNextClick} 
              className={cn(
                "cursor-pointer",
                !hasNextPage && "opacity-50 pointer-events-none"
              )}
              aria-disabled={!hasNextPage}
              tabIndex={hasNextPage ? 0 : -1}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default PaginationControls;
